import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { DollarSign, Users, Target, Award, CheckCircle, Briefcase, TrendingUp, Heart, Globe } from 'lucide-react';

export default function About() {
  const teamMembers = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      bio: 'Former Goldman Sachs executive with 15+ years in fintech innovation.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face',
    },
    {
      name: 'Michael Chen',
      role: 'Chief Technology Officer',
      bio: 'Ex-Google engineer passionate about democratizing financial access.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Customer Success',
      bio: '10+ years helping customers achieve their financial goals.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
    },
  ];

  const values = [
    {
      icon: DollarSign,
      title: 'Trust & Security',
      description: 'Your financial data is protected with bank-level encryption and security protocols.',
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'We prioritize your needs and work tirelessly to provide the best lending experience.',
    },
    {
      icon: Target,
      title: 'Transparency',
      description: 'No hidden fees, no surprises. Clear terms and honest communication always.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every interaction and continuously improve our services.',
    },
  ];

  const timeline = [
    { 
      year: '2018', 
      title: 'Founded',
      event: 'Quick Credit founded with a mission to simplify lending',
      icon: Briefcase,
    },
    { 
      year: '2019', 
      title: 'First Milestone',
      event: 'Reached $100M in funded loans',
      icon: TrendingUp,
    },
    { 
      year: '2020', 
      title: 'Digital Transformation',
      event: 'Launched digital-first application process',
      icon: Globe,
    },
    { 
      year: '2021', 
      title: 'National Expansion',
      event: 'Expanded to all 50 states',
      icon: Target,
    },
    { 
      year: '2022', 
      title: 'Major Achievement',
      event: 'Surpassed $1B in total funded loans',
      icon: Award,
    },
    { 
      year: '2023', 
      title: 'Industry Recognition',
      event: 'Named Best Digital Lender by FinTech Awards',
      icon: Heart,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 gradient-hero">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium mb-6">
              Our Story
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">
              Transforming How People Access Credit
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              We're on a mission to make borrowing simple, transparent, and accessible for everyone. 
              Our technology-driven approach puts you in control of your financial future.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Profile Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl font-bold text-foreground mb-4">
              Meet Our Leadership
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our experienced team brings together decades of expertise in finance, technology, and customer service.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div 
                key={index} 
                className="bg-card rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 text-center animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover rounded-full border-4 border-primary/20 group-hover:border-primary/40 transition-colors"
                  />
                  <div className="absolute inset-0 rounded-full bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-1">{member.name}</h3>
                <p className="text-accent font-medium text-sm mb-3">{member.role}</p>
                <p className="text-muted-foreground text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                Our Mission
              </span>
              <h2 className="font-display text-3xl font-bold text-foreground mb-6">
                Empowering Financial Freedom for Everyone
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                At Quick Credit, we believe everyone deserves access to fair, transparent lending. 
                Traditional lending processes are often slow, confusing, and frustrating. We're changing that.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                By combining cutting-edge technology with a customer-first approach, we've created a 
                lending platform that's fast, simple, and designed with your best interests in mind.
              </p>
              <ul className="space-y-3">
                {['Fast approvals in minutes, not days', 'Competitive rates tailored to you', 'No hidden fees or surprises', '24/7 support when you need it'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-foreground">
                    <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-3 w-3 text-accent" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-6 bg-background rounded-xl shadow-card">
                    <p className="font-display text-3xl font-bold text-primary">$2B+</p>
                    <p className="text-sm text-muted-foreground mt-1">Loans Funded</p>
                  </div>
                  <div className="text-center p-6 bg-background rounded-xl shadow-card">
                    <p className="font-display text-3xl font-bold text-primary">50K+</p>
                    <p className="text-sm text-muted-foreground mt-1">Happy Customers</p>
                  </div>
                  <div className="text-center p-6 bg-background rounded-xl shadow-card">
                    <p className="font-display text-3xl font-bold text-primary">50</p>
                    <p className="text-sm text-muted-foreground mt-1">States Served</p>
                  </div>
                  <div className="text-center p-6 bg-background rounded-xl shadow-card">
                    <p className="font-display text-3xl font-bold text-primary">4.9★</p>
                    <p className="text-sm text-muted-foreground mt-1">Customer Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium mb-6">
              What We Stand For
            </span>
            <h2 className="font-display text-3xl font-bold text-foreground mb-4">
              Our Core Values
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These principles guide everything we do and how we serve our customers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="bg-card rounded-xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <value.icon className="h-6 w-6 text-primary-foreground" />
                </div>
                <h3 className="font-display font-semibold text-foreground mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Timeline Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              Our Journey
            </span>
            <h2 className="font-display text-3xl font-bold text-foreground mb-4">
              Experience Timeline
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From startup to industry leader, here's how we've grown and evolved.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary/30 md:-translate-x-0.5" />
              
              {timeline.map((item, index) => (
                <div 
                  key={index} 
                  className={`relative flex items-start gap-8 mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} animate-slide-up`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'} pl-20 md:pl-0`}>
                    <div className="bg-card rounded-xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 inline-block text-left">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-display text-2xl font-bold text-accent">{item.year}</span>
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full font-medium">{item.title}</span>
                      </div>
                      <p className="text-muted-foreground">{item.event}</p>
                    </div>
                  </div>
                  
                  {/* Timeline node */}
                  <div className="absolute left-8 md:left-1/2 w-6 h-6 -translate-x-1/2 md:-translate-x-1/2 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-lg">
                      <item.icon className="w-3 h-3 text-primary-foreground" />
                    </div>
                  </div>
                  
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
